"use client";

import { motion } from "framer-motion";
import Navbar from "@/app/components/Navbar";
import Footer from "@/app/components/Footer";

const FEATURES = [
  {
    icon: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z",
    title: "Smart Keyword-Based DM Automation",
    description: "Define the exact words that trigger your Instagram auto-reply. When a prospect DMs 'pricing', 'info', or 'demo' — they get an instant, personalised response. No manual inbox monitoring needed.",
    benefits: ["Multiple keyword triggers per automation rule", "Case-insensitive keyword matching", "Per-keyword response templates", "Trigger on DMs and post comments"],
  },
  {
    icon: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z",
    title: "Instant Instagram Auto-Reply to DMs",
    description: "Automate Instagram direct messages that respond the moment a keyword is detected. Every lead gets acknowledged — even at 3am. Turn passive followers into active conversations without lifting a finger.",
    benefits: ["Instant reply on keyword match", "Fully customisable reply text", "Works 24/7 without manual input", "Separate rules per trigger word"],
  },
  {
    icon: "M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z",
    title: "Automated Replies to Post Comments",
    description: "Every comment on your Instagram posts can trigger an auto-reply or a DM. Convert post engagement into private conversations — where the real selling happens.",
    benefits: ["Auto-reply to comments with custom text", "Trigger a follow-up DM from a comment", "Works across all your posts", "Configurable per automation rule"],
  },
  {
    icon: "M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z",
    title: "Multi-Account Instagram Management",
    description: "Connect and manage multiple Instagram business accounts from one dashboard. Ideal for agencies, creators, and brands running more than one profile.",
    benefits: ["Single login for all accounts", "Account-specific automation rules", "Per-account daily usage tracking", "Switch accounts in one click"],
  },
  {
    icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
    title: "Built-In Rate Protection — Stay Safe",
    description: "Instagram automation only works long-term if your account stays healthy. Every rule runs within Instagram's safe messaging limits. Daily caps and automatic token refresh are built in by default.",
    benefits: ["Automatic Instagram rate limiting", "Daily reply cap enforcement", "60-day OAuth token auto-refresh", "No account risk — official API only"],
  },
  {
    icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
    title: "Message Logs & Automation Activity",
    description: "Every automated reply is logged in real time. See which keywords fired, which messages were sent, and how your automations are performing — all from your dashboard.",
    benefits: ["Full log of every automated reply", "Filter by account, date, or rule", "Daily usage tracked automatically", "Pause or edit any rule instantly"],
  },
  {
    icon: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
    title: "100% Instagram-Compliant — Official API Only",
    description: "Aurelius & Co runs entirely on Instagram's official Graph API with OAuth 2.0. No browser automation, no password storage, no risky workarounds. Your account is never at risk.",
    benefits: ["Official Instagram Graph API v20+", "OAuth 2.0 — zero password storage", "Revocable tokens, full account control", "Fully compliant with Meta's platform policies"],
  },
  {
    icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z",
    title: "Simple, Honest Pricing",
    description: "No hidden fees, no long-term lock-in. Pick the plan that fits your volume. Upgrade when you're ready, cancel anytime — no questions asked.",
    benefits: ["Free plan to start", "Monthly billing, no annual lock-in", "Upgrade or cancel anytime", "Usage-based plan tiers"],
  },
];

export default function FeaturesPage() {
  return (
    <>
      <Navbar />
      <main className="min-h-screen px-6 py-32" style={{ background: "#0A0908" }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-6xl mx-auto"
        >
          <div className="text-center mb-16">
            <p
              className="text-xs tracking-[0.3em] uppercase mb-3"
              style={{ color: "rgba(201,168,76,0.7)", fontFamily: "'Montserrat', sans-serif" }}
            >
              Features
            </p>
            <h1
              className="text-4xl md:text-5xl font-light leading-tight mb-4"
              style={{ color: "#F5F0E8", fontFamily: "'Cormorant', serif" }}
            >
              Every feature your Instagram{" "}
              <span style={{ color: "#C9A84C", fontStyle: "italic" }}>automation needs</span>
            </h1>
            <p
              className="text-sm leading-relaxed max-w-xl mx-auto"
              style={{ color: "rgba(245,240,232,0.45)", fontFamily: "'Montserrat', sans-serif" }}
            >
              Built on Instagram's official Graph API. Keyword-triggered DM automation, comment replies, multi-account management — everything works without touching your password or risking your account.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {FEATURES.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="p-8"
                style={{ border: "1px solid rgba(201,168,76,0.1)" }}
              >
                <div
                  className="w-12 h-12 flex items-center justify-center mb-5"
                  style={{ border: "1px solid rgba(201,168,76,0.25)", color: "#C9A84C" }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d={feature.icon} />
                  </svg>
                </div>
                <h2
                  className="text-xl font-light mb-3"
                  style={{ color: "#F5F0E8", fontFamily: "'Cormorant', serif" }}
                >
                  {feature.title}
                </h2>
                <p
                  className="text-sm leading-relaxed mb-5"
                  style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif" }}
                >
                  {feature.description}
                </p>
                <ul className="space-y-2">
                  {feature.benefits.map((benefit) => (
                    <li key={benefit} className="flex items-center gap-2 text-xs" style={{ color: "rgba(245,240,232,0.35)", fontFamily: "'Montserrat', sans-serif" }}>
                      <span style={{ color: "#C9A84C" }}>✓</span>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <p
              className="text-sm mb-4"
              style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif" }}
            >
              Ready to get started?
            </p>
            <a
              href="/login"
              className="inline-block px-8 py-3 text-xs tracking-widest uppercase transition-all duration-300"
              style={{ background: "linear-gradient(135deg, #C9A84C, #E8D5A3)", color: "#0A0908", fontFamily: "'Montserrat', sans-serif" }}
            >
              Start Free Trial
            </a>
          </div>
        </motion.div>
      </main>
      <Footer />
    </>
  );
}
